import { useState, useMemo, useCallback } from 'react';
import { Bell, MessageCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { lsGet, lsSet } from '@/lib/storage';
import { useAuth } from '@/state/auth';
import { toast } from '@/state/toast';
import { formatAgo } from './RemarkCard';
import type { NotificationsResponse } from '@/types/api';

/**
 * Per-user localStorage key для last_seen_at — клиент сам помечает прочитанное.
 * Phase 2B: ключ без префикса бренда; lsGet/lsSet добавляют collegium./consolida.
 * автоматически (dual-read/dual-write).
 */
const LS_LAST_SEEN_PREFIX = 'notif.lastSeenAt:';

function readLastSeen(userEmail: string | undefined): string | null {
  if (!userEmail) return null;
  return lsGet(LS_LAST_SEEN_PREFIX + userEmail);
}
function writeLastSeen(userEmail: string | undefined, iso: string): void {
  if (!userEmail) return;
  lsSet(LS_LAST_SEEN_PREFIX + userEmail, iso);
}

export function BellDropdown() {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const user = useAuth((s) => s.user);
  const userEmail = user?.email;

  // localStorage last_seen_at — клиентский маркер «всё до этого прочитано».
  const [lastSeen, setLastSeen] = useState<string | null>(() => readLastSeen(userEmail));

  // Bell виден только если есть user (т.е. /notifications вернёт 200, а не 401).
  const notifQ = useQuery<NotificationsResponse>({
    queryKey: ['notifications'],
    queryFn: () => api.get<NotificationsResponse>('/notifications?limit=50'),
    enabled: !!user,
    refetchInterval: 60_000, // 1 раз в минуту
    refetchOnWindowFocus: true,
  });

  const items = useMemo(() => notifQ.data?.items || [], [notifQ.data]);

  const unread = useMemo(() => {
    if (!lastSeen) return items.length; // никогда не открывал — всё новое
    return items.filter((n) => (n.submitted_at || '') > lastSeen).length;
  }, [items, lastSeen]);

  const markAll = useCallback(() => {
    const nowIso = new Date().toISOString();
    writeLastSeen(userEmail, nowIso);
    setLastSeen(nowIso);
    toast.success('Все уведомления отмечены прочитанными');
  }, [userEmail]);

  // Если пользователь не залогинен — bell не показываем (не место для логин-промпта).
  if (!user) return null;

  const openItem = (remarkId: string, projectId: string) => {
    setOpen(false);
    // Bell события — это «новый ответ от ГИПа на моё замечание»; ведём в проект.
    nav(`/projects/${projectId}?remark=${remarkId}`);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative text-muted hover:text-ink"
        aria-label="Уведомления"
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
      >
        <Bell size={20} />
        {unread > 0 && (
          <span
            className="absolute flex items-center justify-center rounded-pill tabular"
            style={{
              top: -6, right: -6, minWidth: 16, height: 16, padding: '0 4px',
              background: '#F59E0B', color: '#0A1628',
              fontWeight: 600, fontSize: 10, lineHeight: 1,
            }}
          >{unread}</span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0" style={{ zIndex: 35 }} onClick={() => setOpen(false)} />
          <div
            className="absolute bg-white rounded-card flex flex-col"
            style={{
              top: 'calc(100% + 8px)', right: 0, width: 400,
              border: '1px solid #E5E7EB',
              boxShadow: '0 16px 48px rgba(10,22,40,.16)',
              zIndex: 40, overflow: 'hidden',
            }}
          >
            <div className="flex justify-between items-center" style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>
              <div>
                <div className="font-heading text-ink" style={{ fontSize: 16, fontWeight: 600 }}>Уведомления</div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {notifQ.isLoading ? 'загрузка…' : unread > 0 ? `${unread} непрочитанных` : 'все прочитаны'}
                </div>
              </div>
              {unread > 0 && (
                <button
                  type="button"
                  onClick={markAll}
                  className="text-muted hover:text-ink"
                  style={{ fontSize: 12, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >Отметить все</button>
              )}
            </div>
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              {notifQ.isLoading && (
                <div className="text-muted" style={{ padding: '16px', fontSize: 13, textAlign: 'center' }}>Загрузка…</div>
              )}
              {!notifQ.isLoading && items.length === 0 && (
                <div className="text-muted" style={{ padding: '24px 16px', fontSize: 13, textAlign: 'center' }}>
                  Пока нет ответов на ваши замечания.
                </div>
              )}
              {items.map((n) => {
                const isUnread = !lastSeen || (n.submitted_at || '') > lastSeen;
                const sectionLabel = n.section_code ? ` · раздел ${n.section_code}` : '';
                const projectLabel = n.project_name || n.project_id.slice(0, 8);
                return (
                  <button
                    key={n.event_id}
                    type="button"
                    onClick={() => openItem(n.remark_id, n.project_id)}
                    className="flex items-start w-full text-left"
                    style={{
                      padding: '12px 16px', gap: 12,
                      background: isUnread ? 'rgba(245,158,11,.05)' : 'transparent',
                      border: 'none',
                      borderBottom: '1px solid #E5E7EB',
                      cursor: 'pointer',
                    }}
                  >
                    <MessageCircle size={18} stroke="#6B7280" className="shrink-0 mt-0.5" />
                    <div style={{ flex: 1 }}>
                      <div className="text-ink" style={{ fontSize: 14, fontWeight: isUnread ? 600 : 500 }}>
                        Новый ответ по замечанию №{n.remark_id.slice(0, 8)}
                      </div>
                      <div className="text-muted mt-0.5" style={{ fontSize: 12, lineHeight: 1.4 }}>
                        {projectLabel}{sectionLabel}
                      </div>
                      {n.remark_body && (
                        <div
                          className="text-muted mt-1"
                          style={{
                            fontSize: 11, lineHeight: 1.4,
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                          }}
                        >{n.remark_body}</div>
                      )}
                      <div className="text-muted mt-1 tabular" style={{ fontSize: 11 }}>{formatAgo(n.submitted_at)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            {items.length > 0 && (
              <div className="flex justify-center text-muted" style={{ padding: '8px 16px', borderTop: '1px solid #E5E7EB', background: '#F3F4F6', fontSize: 12 }}>
                Полный журнал — Настройки проекта → Аудит
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
